import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MarkdownView from "@/components/MarkdownView";
import SEO from "@/components/SEO";
import { Clock, Lock, ArrowLeft, ArrowRight, BookOpen } from "lucide-react";

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // When the article is missing, flip the meta robots to `noindex` so
  // Google removes any stale soft-404 impressions. When present, surface
  // title / description / canonical / OG tags so the crawler sees real
  // content on the client-rendered page.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/blogs/${slug}`)
      .then((r) => { if (!cancelled) setBlog(r.data); })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-kb-bg">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-20 kb-mono text-xs text-kb-ink-muted">Loading…</div>
      </div>
    );
  }
  if (notFound || !blog) {
    return (
      <div className="min-h-screen bg-kb-bg">
        <SEO title="Article not found — KhojByte" description="The article you're looking for isn't available." path={`/blogs/${slug}`} noindex />
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-20 text-center">
          <h1 className="font-serif text-3xl text-kb-ink">Article not found</h1>
          <Link to="/blogs" className="kb-btn-outline mt-6 inline-flex text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to journal
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kb-bg" data-testid="blog-detail-page">
      <SEO
        title={`${blog.title} — KhojByte`}
        description={blog.excerpt || blog.meta_description || `${blog.title} · KhojByte Research Lab`}
        path={`/blogs/${blog.slug || slug}`}
        image={blog.cover_image_url}
      />
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 pt-12 pb-24">
        <Link to="/blogs" className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted inline-flex items-center gap-1 hover:text-kb-primary">
          <ArrowLeft className="w-3 h-3" /> Back to journal
        </Link>

        <div className="mt-6 kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">
          {blog.domain || "JOURNAL"} · {blog.read_minutes || 5} MIN READ
        </div>
        <h1 className="font-serif text-4xl lg:text-5xl text-kb-ink mt-3 leading-tight">{blog.title}</h1>
        {blog.excerpt && <p className="mt-5 text-lg text-kb-ink-muted leading-relaxed">{blog.excerpt}</p>}

        <div className="mt-6 flex items-center gap-3 pb-6 border-b border-slate-200">
          {blog.author_picture ? (
            <img src={blog.author_picture} alt="" className="w-11 h-11 rounded-full object-cover border border-slate-200" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-kb-primary/10 flex items-center justify-center kb-mono text-sm text-kb-primary">
              {(blog.author_name || "K")[0]?.toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm text-kb-ink">{blog.author_name || "KhojByte Team"}</div>
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
              {blog.author_role || "Research"}
              {blog.published_at ? ` · ${new Date(blog.published_at).toLocaleDateString()}` : ""}
            </div>
          </div>
          <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {blog.read_minutes || 5} min
          </span>
        </div>

        {blog.cover_image_url && (
          <div className="mt-8 rounded-sm overflow-hidden border border-slate-200">
            <img src={blog.cover_image_url} alt="" className="w-full h-auto block" />
          </div>
        )}

        <article className="mt-8" data-testid="blog-body">
          {blog.body_md ? (
            <MarkdownView source={blog.body_md} />
          ) : (
            <div className="text-kb-ink-muted text-sm italic">No content yet.</div>
          )}
        </article>

        {blog.locked && (
          <div className="mt-10 kb-card p-8 text-center" data-testid="blog-locked-cta">
            <Lock className="w-7 h-7 text-kb-primary mx-auto" strokeWidth={1.5} />
            <h3 className="font-serif text-2xl text-kb-ink mt-3">Premium article</h3>
            <p className="text-sm text-kb-ink-muted mt-2 max-w-md mx-auto">
              You&apos;ve read the free preview. Subscribe to KhojByte Premium to unlock the full article and the rest of the
              journal.
            </p>
            <Link to="/pricing" className="kb-btn-primary mt-5 inline-flex">
              View plans <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {(blog.tags?.length > 0 || blog.technologies?.length > 0) && (
          <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap gap-2">
            {(blog.tags || []).map((t) => (
              <span key={`tag-${t}`} className="kb-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-sm border border-slate-200 bg-white text-kb-ink-muted">
                {t}
              </span>
            ))}
            {(blog.technologies || []).map((t) => (
              <span key={`tech-${t}`} className="kb-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-sm border border-kb-primary/30 bg-kb-primary/5 text-kb-primary">
                {t}
              </span>
            ))}
          </div>
        )}

        <div className="mt-12 flex items-center justify-between gap-3 flex-wrap">
          <Link to="/blogs" className="kb-btn-outline text-sm">
            <ArrowLeft className="w-3.5 h-3.5" /> More articles
          </Link>
          <Link to="/research-ideas" className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1">
            <BookOpen className="w-3.5 h-3.5" /> Explore research ideas
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
